import React, { FC, HTMLAttributes } from 'react'

import clsx from 'clsx'

import { Heading } from '../Heading/Heading'

type IconName = 'search' | 'file' | 'list' | 'task' | 'chart'
type IllustrationName = 'card' | 'list' | 'chart' | 'key'
export interface EmptyProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: IconName
  illustration?: IllustrationName
  theme?: 'default' | 'center' | 'illustration'
  title?: string
  children?: React.ReactNode
  asCard?: boolean
  assetHost?: string
}

export const EmptyStyle = {
  _: 'w-fill border border-border-muted rounded-lg flex flex-col items-start justify-center md:flex-row md:items-start md:justify-start gap-y-4 md:gap-y-0',
  asCard: {
    false: 'bg-background-alternative',
    true: 'bg-background-default lg:py-16'
  },
  theme: {
    center: {
      _: 'text-center py-8 px-16',
      content: 'mx-auto max-w-lg',
      icon: 'mb-8 flex justify-center',
      title: 'mb-2'
    },
    default: {
      _: 'flex p-4 md:p-8',
      content: 'max-w-lg',
      icon: 'mr-8 shrink-0 grow-0',
      title: 'mb-0'
    },
    illustration: {
      _: 'md:flex p-4 md:p-8 lg:px-16 items-center',
      content: 'max-w-xlg',
      icon: 'mb-4',
      title: 'mb-2'
    }
  }
}

interface IconProps extends Pick<EmptyProps, 'assetHost'> {
  iconName: IconName | undefined
}

function LargeIcon({ assetHost = '', iconName }: IconProps) {
  if (iconName == 'chart') return <img src={`${assetHost}/svg/icons/ChartIconLarge.svg`} />
  if (iconName == 'task') return <img src={`${assetHost}/svg/icons/TaskIconLarge.svg`} />
  if (iconName == 'search') return <img src={`${assetHost}/svg/icons/SearchIconLarge.svg`} />
  if (iconName == 'list') return <img src={`${assetHost}/svg/icons/ListIconLarge.svg`} />
  if (iconName == 'file') return <img src={`${assetHost}/svg/icons/FileIconLarge.svg`} />
  return <img src={`${assetHost}/svg/icons/TaskIconLarge.svg`} />
}

function SmallIcon({ assetHost = '', iconName }: IconProps) {
  if (iconName == 'chart') return <img src={`${assetHost}/svg/icons/ChartIconSmall.svg`} />
  if (iconName == 'task') return <img src={`${assetHost}/svg/icons/TaskIconSmall.svg`} />
  if (iconName == 'search') return <img src={`${assetHost}/svg/icons/SearchIconSmall.svg`} />
  if (iconName == 'list') return <img src={`${assetHost}/svg/icons/ListIconSmall.svg`} />
  if (iconName == 'file') return <img src={`${assetHost}/svg/icons/FileIconSmall.svg`} />
  return <img src={`${assetHost}/svg/icons/TaskIconSmall.svg`} />
}

interface IllustrationProps extends Pick<EmptyProps, 'assetHost'> {
  illustrationName: IllustrationName
}

function Illustration({ assetHost = '', illustrationName }: IllustrationProps) {
  if (illustrationName == 'card') return <img src={`${assetHost}/svg/illustrations/CardIllustration.svg`} />
  if (illustrationName == 'chart') return <img src={`${assetHost}/svg/illustrations/ChartIllustration.svg`} />
  if (illustrationName == 'list') return <img src={`${assetHost}/svg/illustrations/ListIllustration.svg`} />
  if (illustrationName == 'key') return <img src={`${assetHost}/svg/illustrations/KeyIllustration.svg`} />
  return <img src={`${assetHost}/svg/illustrations/ListIllustration.svg`} />
}
/**
 * Empty - Empty UI states make sure the user knows they aren’t experiencing an issue with the page loading. Anytime we have nothing to show an empty UI message should be used. They often can be paired with a primary button below them to prompt the user to act.
 */
export const Empty: FC<EmptyProps> = React.forwardRef<HTMLDivElement, EmptyProps>(
  (
    { asCard = 'false', assetHost, children, className, icon, illustration, theme = 'default', title, ...props },
    ref
  ) => {
    const EmptyContent = (
      <>
        <div className={clsx(EmptyStyle.theme[theme].icon)}>
          {icon && theme === 'center' ? (
            <LargeIcon assetHost={assetHost} iconName={icon} />
          ) : (
            <SmallIcon assetHost={assetHost} iconName={icon} />
          )}
        </div>
        <div className={clsx(EmptyStyle.theme[theme].content)}>
          {title ? (
            <Heading className={clsx(EmptyStyle.theme[theme].title)} theme='card'>
              {title}
            </Heading>
          ) : null}
          {children}
        </div>
      </>
    )

    return (
      <div
        ref={ref}
        {...props}
        className={clsx(EmptyStyle._, EmptyStyle.asCard[String(asCard)], EmptyStyle.theme[theme]._, className)}
      >
        {illustration ? (
          <>
            <div className='mb-8 mr-8 md:mb-0'>{EmptyContent}</div>
            <div className='flex grow justify-center'>
              <Illustration assetHost={assetHost} illustrationName={illustration} />
            </div>
          </>
        ) : (
          <>{EmptyContent}</>
        )}
      </div>
    )
  }
)

Empty.displayName = 'Empty'
